/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import {
  AfterViewInit, Component, ContentChildren,
  ElementRef, EventEmitter, Input,
  Output, QueryList, ViewChild,
} from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';

import { debounceTime } from 'rxjs/operators';

import { ButtonSize, ButtonStyle } from '../aero-button/aero-button.component';
import { TranslationService } from '../../common/translation.service';

/**
 * Component for our aero-form field.
 */
@Component({
  selector: 'aero-form',
  templateUrl: './aero-form.component.html',
  standalone: false,
})
export class AeroFormComponent implements AfterViewInit {
  /**
   * Creates a top-level FormGroup instance and binds it to a form to track aggregate form value and validation status.
   */
  @ViewChild(NgForm) public form: NgForm;

  /**
   * To get all the elements/NgModel of the aero-form.
   */
  @ContentChildren(NgModel, { descendants: true }) public models: QueryList<NgModel>;

  /**
   * Event emitter for cancel to emit the event to the parent component.
   */
  @Output() public onCancel: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Event emitter for submit to emit the event to the parent component.
   */
  @Output() public onSubmit: EventEmitter<NgForm> = new EventEmitter<NgForm>();

  /**
   * To add the cancel button on the aero-form.
   */
  @Input() public showCancelButton : boolean = false;

  /**
   * To add the submit button on the aero-form.
   */
  @Input() public showSubmitButton : boolean = true;

  /**
   * Set the label for the submit button.
   */
  @Input() public submitButtonLabel : string;

  /**
   * Set the style of the cancel button. Default value is text grey.
   */
  @Input() public cancelButtonStyle: ButtonStyle = ButtonStyle.TEXT_GREY;

  /**
   * To handle the behavior of save button when the input field is changed.
   */
  @Input() public updateSaveButtonStateOnChange: boolean = true;

  /**
   * Set the value as true to disable the save the button.
   */
  public saveButtonDisabled : boolean = true;

  /**
   * NodeList of all the potential aero-inputs associated with the aero-form. Will house all NgModels.
   * @private
   */
  private aeroInputs : any;

  /**
   * Map of NodeList to their respective NgModel.
   * @private
   */
  private viewToModelMap : Map< NgModel, any > = new Map< NgModel, any >();

  /**
   * Button style of aero button
   */
  @Input() public buttonStyle : ButtonStyle = ButtonStyle.SAVE;

  /**
   * Button size of aero button
   */
  public buttonSize : ButtonSize = ButtonSize.LARGER;

  /**
   * Default Constructor.
   */
  constructor(private elementReference : ElementRef, private $translate : TranslationService) {}

  /**
   * A lifecycle hook that is called after view initialized.
   */
  ngAfterViewInit(): void {
    this.aeroInputs = jQuery(this.elementReference.nativeElement.querySelectorAll('aero-input'));
    // Adding the ngModel fields to the form.
    this.models.toArray().forEach((model : NgModel, index) => {
      this.form.addControl(model);
      this.viewToModelMap.set(model, this.aeroInputs[index]);
      model.statusChanges
        .pipe(debounceTime(500))
        .subscribe((data) => {
          if (this.updateSaveButtonStateOnChange) {
            this.updateSaveButtonState();
          }
          const $aeroInputElement = jQuery(this.viewToModelMap.get(model));
          const $errorIconTooltip = $aeroInputElement.find('.right');
          const $aeroInputErrorWrapper = $aeroInputElement.find('.custom-code-error-message');
          if (model.errors) {
            if (model.errors.customErrorCode) {
              $aeroInputElement.find('input').addClass('aero-input-invalid');
              $errorIconTooltip.show();
              const $spanError = $('<span />').addClass('bulb-bottom-error-msg')
                .html(this.$translate.translateInstant(model.errors.code));
              $aeroInputErrorWrapper.append($spanError);
              $aeroInputErrorWrapper.show();
            } else {
              $aeroInputErrorWrapper.find('.bulb-bottom-error-msg').remove();
            }
          } else if (model.errors === null) {
            $aeroInputElement.find('input').removeClass('aero-input-invalid');
            $aeroInputErrorWrapper.find('.bulb-bottom-error-msg').remove();
            $errorIconTooltip.hide();
          }
        });
    });
  }

  /**
   * On form submit emit the onSubmit event.
   *
   * @param form NgForm
   */
  submit(form: NgForm): void {
    this.onSubmit.emit(form);
  }

  /**
   * Method that disabled or enables the save button for the aero-form;
   * @param disabled
   */
  updateSaveButtonState(disabled : boolean = true) : void {
    this.saveButtonDisabled = (!this.form.valid || this.form.pristine) && disabled;
  }

  /**
   * On form cancel emit the onCancel event.
   */
  cancel() {
    this.resetFormFields();
    this.onCancel.emit();
  }

  /**
   * Reset and clear error properties.
   */
  resetFormFields() {
    this.models.toArray().forEach((model : NgModel, index) => {
      // check if the resetField method exist.
      // @ts-ignore
      // FIX: check for correct type here. (remove the ts-ignore comment)
      if (typeof model.valueAccessor.resetField === 'function') {
        // @ts-ignore
        model.valueAccessor.resetField();
      }
    });
  }

  /**
   * Helper method to mark the aero from as pristine.
   */
  markAeroFormAsPristine() : void {
    this.form.form.markAsPristine();
  }

  /**
   * Method to reset all form model controls to original values.
   * @param initialValues
   */
  resetAeroFormToInitialValues(initialValues : any) : void {
    this.form.form.reset(initialValues);
  }

  /**
   * Method to set form values.
   * @param values
   */
  patchAeroFormValues(values : any) : void {
    /**
     * Using `patchValue()` instead of `setValue()` -- since the `setValue()` method will set ***all*** Formcontrol
     * values from model. If you do not mention any of the Formcontrol values in model, then it will throw an exception.
     * The `patchValue()` method will also set Formcontrol values from a model but only for those which we
     * have mentioned in the model. So, it is not necessary to pass all the model info in patchValue() method.
     *
     * This is key, since some of our models in the resume sub-sections are not necessary to pass. Like the `endDate`
     * in the Experience section for instance.
     */
    this.form.form.patchValue(values);
  }
}
